import { createFileRoute } from "@tanstack/react-router";
import { useSync } from "@tldraw/sync";
import { type TLAssetStore, Tldraw } from "tldraw";
import "tldraw/tldraw.css";
import { useEffect, useMemo, useState } from "react";
import { TLDRAW_KEY } from "@/src/config.browser";
import { trpc } from "../api/trpcClient";
import { DrawPeopleMenu } from "../components/DrawPeopleMenu";

// Simple in-memory asset store for development
// In production, this should upload to cloud storage
const assetStore: TLAssetStore = {
  async upload(_asset, file) {
    // For now, convert to base64 data URL for inline storage
    // TODO: Implement proper cloud storage in production
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ src: reader.result as string });
      };
      reader.readAsDataURL(file);
    });
  },
};

// Custom SharePanel component for tldraw
function SharePanel() {
  return (
    <div className="tlui-share-zone" draggable={false}>
      <DrawPeopleMenu />
    </div>
  );
}

function DrawCanvas() {
  // Get current user data
  const { data: currentUser } = trpc.users.me.useQuery();

  // Check if using default token and log remaining days
  useEffect(() => {
    const isDefaultToken =
      !import.meta.env?.VITE_TLDRAW_KEY && !globalThis.process?.env?.VITE_TLDRAW_KEY;

    if (isDefaultToken) {
      const tokenCreatedDate = new Date("2025-11-19");
      const trialDays = 100;
      const expiryDate = new Date(tokenCreatedDate);
      expiryDate.setDate(expiryDate.getDate() + trialDays);

      const now = new Date();
      const remainingMs = expiryDate.getTime() - now.getTime();
      const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

      console.log(
        `⚠️ Using default tldraw token (created ${tokenCreatedDate.toISOString().split("T")[0]})`,
      );
      console.log(
        `📅 Trial expires: ${expiryDate.toISOString().split("T")[0]} (${remainingDays} days remaining)`,
      );
      console.log(`💡 Set VITE_TLDRAW_KEY environment variable to use a custom token`);
    }
  }, []);

  // Generate a room ID based on the route or use a default
  const roomId = "team-dash-draw";

  // Fetch WebSocket backend URL from server
  const [wsBackendUrl, setWsBackendUrl] = useState<string | null>(null);

  useEffect(() => {
    // Fetch the WebSocket URL from the backend
    fetch("/api/websocket-url")
      .then((res) => res.json())
      .then((data) => {
        if (data.url) {
          setWsBackendUrl(data.url);
        } else {
          console.error("Failed to get WebSocket URL:", data);
          // Fallback to current host if API fails
          const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
          const host = window.location.host;
          setWsBackendUrl(`${protocol}//${host}`);
        }
      })
      .catch((error) => {
        console.error("Error fetching WebSocket URL:", error);
        // Fallback to current host if fetch fails
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const host = window.location.host;
        setWsBackendUrl(`${protocol}//${host}`);
      });
  }, []);

  // Construct full WebSocket URL with room ID
  const wsUrl = useMemo(() => {
    if (!wsBackendUrl) return null;
    return `${wsBackendUrl}/api/tldraw/connect/${roomId}`;
  }, [wsBackendUrl]);

  // Create sync store - only when we have the WebSocket URL
  const store = useSync({
    uri: wsUrl || "", // Provide empty string as fallback
    assets: assetStore,
    // Use Better Auth user data if available
    userInfo: currentUser
      ? {
          id: currentUser.id,
          name: currentUser.name,
          color: undefined, // Let tldraw assign a color
        }
      : undefined,
  });

  // Custom components for tldraw
  const components = useMemo(
    () => ({
      SharePanel,
    }),
    [],
  );

  // Show loading state while fetching WebSocket URL
  if (!wsUrl) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div>Loading canvas...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0 }}>
      <Tldraw store={store} licenseKey={TLDRAW_KEY} components={components} />
    </div>
  );
}

export const Route = createFileRoute("/draw")({
  component: DrawCanvas,
});
